import { GetUserResponseDtoFeatureFlagsValue } from '@issuemanager/api-client';
import { useUserContext } from '@issuemanager/apps/client/contexts';
import { FeatureFlagKey } from '@issuemanager/constants';

/**
 * Hook to access feature flags.
 *
 * @param {FeatureFlagKey} [key] - Optional feature flag key to check
 *   (e.g., 'draftIssues', 'pendo', 'issueRelationships')
 * @returns {boolean | Record<string, GetUserResponseDtoFeatureFlagsValue> | undefined}
 *   - If key is provided: returns boolean indicating if the feature is enabled
 *   - If key is not provided: returns the entire feature flags object
 *
 * @example
 * // Get a specific feature flag as a boolean (with autocomplete and type safety)
 * const isDraftIssuesEnabled = useFeatureFlags('draftIssues');
 * const isPendoEnabled = useFeatureFlags('pendo');
 *
 * @example
 * // Get all feature flags
 * const featureFlags = useFeatureFlags();
 */
export function useFeatureFlags(
  key?: FeatureFlagKey,
): boolean | Record<string, GetUserResponseDtoFeatureFlagsValue> | undefined {
  const { data: userData } = useUserContext();
  const featureFlags = userData?.featureFlags;

  if (key !== undefined) {
    return featureFlags?.[key]?.enabled ?? false;
  }

  return featureFlags;
}
